import classNames from 'classnames';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

type IconType = ComponentProps<typeof FontAwesomeIcon>['icon'];

export type InviteBullet = { key: string; icon: IconType; label: ReactNode };
type InviteReward = {
  icon: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
};

type Props = {
  header: ReactNode;
  /**
   * Per-viewport banner art; the URLs are resolved by the surrounding page. A
   * viewport with no art shows `bannerFallbackColor` alone.
   */
  bannerImageUrls: { mobile?: Nullable<string>; desktop?: Nullable<string> };
  bannerFallbackColor: string;
  /** One image already composing the whole card artwork, when there is one. */
  cardsImageUrl?: Nullable<string>;
  /** Fallback to `cardsImageUrl`: three separate cards laid out as a fan. */
  cardImageUrls?: string[];
  inviterNameColor: string;
  modeLabel: ReactNode;
  inviterName: string;
  bullets: InviteBullet[];
  reward: InviteReward;
  affiliateBadgeImageUrl?: string | null;
  ctaLabel: ReactNode;
  ctaHref: string;
  className?: string;
};

const root = css`
  min-height: var(--100vh);
  background-image: var(--banner-overlay), var(--banner-mobile, none);
  background-position: top center;
  background-size: cover;
  background-repeat: no-repeat;
  @media ${laptopAndAbove} {
    background-image: var(--banner-overlay), var(--banner-desktop, none);
  }
`;

const composedCards = css`
  width: 100%;
  max-width: 343px;
  height: auto;
`;

const cardFan = css`
  position: relative;
  width: 100%;
  max-width: 343px;
  /* Tall enough for the centre card so the fan never overlaps the header. */
  height: 200px;
`;

const fanCard = css`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 100px;
  height: auto;
  border-radius: 6px;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.45));
  &.left {
    translate: calc(-50% - 92px) -14px;
    rotate: -8deg;
  }
  &.right {
    translate: calc(-50% + 92px) -14px;
    rotate: 8deg;
  }
  &.center {
    width: 122px;
    translate: -50% 0;
    z-index: 1;
  }
`;

const badge = css`
  /*
   * Out of flow and centred on the block's top edge (half over, half outside) so
   * the card fan keeps the exact same position as the non-affiliate variant.
   */
  position: absolute;
  top: 0;
  left: 50%;
  translate: -50% -50%;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  z-index: 3;
`;

const card = css`
  position: relative;
  /* Tuck the card fan behind the top of the card, as in the design. */
  margin-top: calc(-3 * var(--unit));
  /* Above the fan (the centre card raises itself to stack within it). */
  z-index: 2;
  padding: var(--triple-unit);
  background: var(--c-nl-500);
  backdrop-filter: blur(24px);
  border-radius: 20px;
  border: 1px solid var(--c-nd-150);
  /* Reserve room for the affiliate badge straddling the top edge. */
  &.with-badge {
    padding-top: calc(7 * var(--unit));
  }
  & .inviter-name {
    color: var(--inviter-name-color);
  }
`;

const rewardBox = css`
  border: 1px solid var(--c-green-400);
  border-radius: 12px;
`;

const cta = css`
  max-width: 343px;
`;

/** Card images are given in visual order; `.center` stacks in front via z-index. */
const FAN_POSITIONS = ['left', 'center', 'right'];

const CardArt = ({
  imageUrl,
  fanImageUrls,
}: {
  imageUrl?: Nullable<string>;
  fanImageUrls: string[];
}) => {
  if (imageUrl) {
    return <img aria-hidden className={composedCards} src={imageUrl} alt="" />;
  }

  if (fanImageUrls.length === 0) {
    return null;
  }

  return (
    <div aria-hidden className={classNames(cardFan, 'mx-auto')}>
      {FAN_POSITIONS.map((position, index) =>
        fanImageUrls[index] ? (
          <img
            key={position}
            className={classNames(fanCard, position)}
            src={fanImageUrls[index]}
            alt=""
          />
        ) : null
      )}
    </div>
  );
};

export const SetInviteLanding = ({
  header,
  bannerImageUrls,
  bannerFallbackColor,
  cardsImageUrl,
  cardImageUrls = [],
  inviterNameColor,
  modeLabel,
  inviterName,
  bullets,
  reward,
  affiliateBadgeImageUrl,
  ctaLabel,
  ctaHref,
  className,
}: Props) => (
  <Vertical
    center
    gap={2}
    className={classNames('w-full mx-auto px-2 py-4', root, className)}
    style={
      {
        backgroundColor: bannerFallbackColor,
        '--banner-overlay':
          'linear-gradient(to bottom, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.45) 100%)',
        ...(bannerImageUrls.mobile && {
          '--banner-mobile': `url(${bannerImageUrls.mobile})`,
        }),
        ...(bannerImageUrls.desktop && {
          '--banner-desktop': `url(${bannerImageUrls.desktop})`,
        }),
        '--inviter-name-color': inviterNameColor,
      } as CSSProperties
    }
  >
    {header}
    <Vertical grow center gap={0} className="w-full">
      <CardArt imageUrl={cardsImageUrl} fanImageUrls={cardImageUrls} />
      <Vertical
        gap={3}
        className={classNames('w-full max-w-[343px] mx-auto', card, {
          'with-badge': !!affiliateBadgeImageUrl,
        })}
      >
        {!!affiliateBadgeImageUrl && (
          <img className={badge} src={affiliateBadgeImageUrl} alt="" />
        )}
        <Vertical gap={2} center>
          <BrandM as="h1" color="var(--c-white)" className="text-center">
            <FormattedMessage
              defaultMessage="{name} invites you to play Sorare {mode}!"
              values={{
                name: <span className="inviter-name">{inviterName}</span>,
                mode: modeLabel,
              }}
            />
          </BrandM>
          <Vertical gap={1} center>
            {bullets.map(({ key, icon, label }) => (
              <Horizontal key={key} gap={1} center>
                <FontAwesomeIcon
                  icon={icon}
                  size="sm"
                  color="var(--c-nd-600)"
                />
                <LabelM color="var(--c-nd-600)">{label}</LabelM>
              </Horizontal>
            ))}
          </Vertical>
        </Vertical>
        <Horizontal
          gap={1}
          center
          className={classNames('w-full p-1.5', rewardBox)}
        >
          {reward.icon}
          <Vertical gap={0}>
            <LabelS bold color="var(--c-green-400)" className="uppercase">
              {reward.title}
            </LabelS>
            <LabelS color="var(--c-nd-600)">{reward.subtitle}</LabelS>
          </Vertical>
        </Horizontal>
      </Vertical>
    </Vertical>
    <Button
      size="large"
      color="secondary"
      fullWidth
      href={ctaHref}
      externalLink
      className={cta}
    >
      {ctaLabel}
    </Button>
  </Vertical>
);
